// components/Dashboard/SyntheticAssetsManager.js
import React, { useState, useEffect, useRef } from 'react';
import { 
  getSyntheticAssets, 
  updateSyntheticAsset, 
  transferSyntheticAsset, 
  deleteSyntheticAsset,
  getSyntheticAssetFields
} from '../../services/api';

const SyntheticAssetsManager = ({ 
  selectedAccount, 
  selectedBroker, 
  selectedAsset,  
  onAssetTransferred 
}) => {
  const [syntheticAssets, setSyntheticAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [formData, setFormData] = useState({});
  const [fieldInfo, setFieldInfo] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  
  const contentRef = useRef(null);

  // Helper functions for side formatting
  const formatSideForDisplay = (side) => {
    if (!side || side === 'TBD') return 'Not set';
    return side.toLowerCase() === 'buy' ? 'Buy' : 'Sell';
  };

  const formatFieldForDisplay = (value) => {
    if (!value || value === 'TBD') return 'Not set';
    return value;
  };

  // Загрузка синтетических активов
  useEffect(() => {
    if (selectedAccount && selectedBroker) {
      loadSyntheticAssets();
    }
  }, [selectedAccount, selectedBroker, selectedAsset]); 
  const loadSyntheticAssets = async () => {
    setLoading(true);
    try {
      const assets = await getSyntheticAssets(selectedBroker, selectedAccount);
      if (assets) {
        let filteredAssets = assets;
        
        if (selectedAsset && selectedAsset.is_synthetic) {
          filteredAssets = assets.filter(asset => 
            asset.symbol === selectedAsset.symbol && 
            asset.broker === selectedAsset.broker &&
            (selectedAsset.account ? asset.account === selectedAsset.account : true)
          );
        } else if (selectedAsset && !selectedAsset.is_synthetic) {
          filteredAssets = [];
        }
        
        setSyntheticAssets(filteredAssets);
        if (filteredAssets.length > 0) {
          setIsOpen(true);
        }
      }
    } catch (error) {
      console.error('Error loading synthetic assets:', error);
      setSyntheticAssets([]);
    } finally {
      setLoading(false);
    }
  };

  const loadFieldInfo = async (assetId) => {
    try {
      const info = await getSyntheticAssetFields(assetId);
      if (info) {
        setFieldInfo(info);
      }
    } catch (error) {
      console.error('Error loading field info:', error);
    }
  };
  
  const handleEdit = async (asset) => {
    setEditingAsset(asset);
    setFormData({
      side: (asset.side && asset.side !== 'TBD') ? asset.side : '',
      currency: (asset.currency && asset.currency !== 'TBD') ? asset.currency : '',
      exchange: (asset.exchange && asset.exchange !== 'TBD') ? asset.exchange : '',
      sectype: (asset.sectype && asset.sectype !== 'TBD') ? asset.sectype : '',
      leverage: asset.leverage || 1.0,
      isAssetActive: asset.isAssetActive
    });
    await loadFieldInfo(asset.id);
  };

  const handleSave = async () => {
    if (!editingAsset) return;
  
    try {
      const dataToSave = {
        id: editingAsset.id,
        side: formData.side || 'TBD',
        currency: formData.currency || 'TBD',
        exchange: formData.exchange || 'TBD', 
        sectype: formData.sectype || 'TBD',
        leverage: formData.leverage || 1.0,
        isAssetActive: formData.isAssetActive
      };
      
      const result = await updateSyntheticAsset(dataToSave);
      
      if (result) {
        setEditingAsset(null);
        setFormData({});
        setFieldInfo(null);
        await loadSyntheticAssets();
        alert('Synthetic asset updated successfully!');
      } else {
        alert('Failed to update synthetic asset');
      }
    } catch (error) {
      console.error('Error updating synthetic asset:', error);
      alert('Failed to update synthetic asset');
    }
  };

  const handleCancel = () => {
    setEditingAsset(null);
    setFormData({});
    setFieldInfo(null);
  };

  const handleTransfer = async (asset) => {
    if (!asset.is_ready_for_transfer) {
      alert('Asset is not ready for transfer. Please fill all required fields.');
      return;
    }
  
    try {
      const result = await transferSyntheticAsset({
        id: asset.id,
        broker: asset.broker,
        account: asset.account
      });
      
      if (result) {
        await loadSyntheticAssets();
        onAssetTransferred && onAssetTransferred(asset);
        alert('Asset transferred successfully!');
      } else {
        alert('Failed to transfer asset');
      }
    } catch (error) {
      console.error('Error transferring asset:', error);
      alert('Failed to transfer asset');
    }
  };

  const handleDelete = async (asset) => {
    if (!window.confirm(`Are you sure you want to delete ${asset.symbol}?`)) {
      return;
    }
  
    try {
      const result = await deleteSyntheticAsset(asset.id);
      
      if (result) {
        await loadSyntheticAssets();
        alert('Asset deleted successfully!');
      } else {
        alert('Failed to delete asset');
      }
    } catch (error) {
      console.error('Error deleting asset:', error);
      alert('Failed to delete asset');
    }
  };

  const renderEditForm = () => {
    if (!editingAsset || !fieldInfo) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
          <h3 className="text-lg font-semibold mb-4">
            Edit Synthetic Asset: {editingAsset.symbol}
          </h3>
          
          <div className="space-y-4">
            {/* Side */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Side *</label>
              <select 
                value={formData.side || ''}
                onChange={(e) => setFormData({...formData, side: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              >
                <option value="">Select side</option>
                <option value="BUY">Buy</option>
                <option value="SELL">Sell</option>
              </select>
            </div>

            {/* Currency (for IB) */}
            {editingAsset.broker === 'ib' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency *</label>
                <input
                  type="text"
                  value={formData.currency || ''}
                  onChange={(e) => setFormData({...formData, currency: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
            )}

            {/* Exchange (for IB) */}
            {editingAsset.broker === 'ib' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Exchange *</label>
                <input
                  type="text"
                  value={formData.exchange || ''}
                  onChange={(e) => setFormData({...formData, exchange: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
            )}

            {/* Sectype (for IB) */}
            {editingAsset.broker === 'ib' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sectype *</label>
                <input
                  type="text"
                  value={formData.sectype || ''}
                  onChange={(e) => setFormData({...formData, sectype: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
            )}

            {/* Leverage (for OKX) */}
            {editingAsset.broker === 'okx' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Leverage *</label>
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="100"
                  value={formData.leverage || 1.0}
                  onChange={(e) => setFormData({...formData, leverage: parseFloat(e.target.value)})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
            )}

            {/* Asset Active */}
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isAssetActive || false}
                  onChange={(e) => setFormData({...formData, isAssetActive: e.target.checked})}
                  className="mr-2"
                />
                Asset Active
              </label>
            </div>

            {/* Progress indicator */}
            {fieldInfo && (
              <div className="mt-4 p-3 bg-gray-50 rounded">
                <h4 className="font-medium mb-2">Field Status:</h4>
                <div className="text-sm space-y-1">
                  {Object.entries(fieldInfo.fields).map(([field, info]) => (
                    <div key={field} className="flex justify-between">
                      <span className="capitalize">{field.replace('_', ' ')}:</span>
                      <span className={info.filled ? 'text-green-600' : 'text-red-600'}>
                        {info.filled ? '✓' : '✗'}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-sm">
                  Ready for transfer: {fieldInfo.is_ready_for_transfer ? 
                    <span className="text-green-600">Yes</span> : 
                    <span className="text-red-600">No</span>
                  }
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-gray-600 border rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`max-w-sm mx-auto bg-white shadow-[0_10px_30px_rgba(0,0,0,0.15)] rounded-lg p-6 mt-4`}>
        <div className="flex justify-center items-center h-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (syntheticAssets.length === 0) {
    return null;
  }

  return (
    <div className={`max-w-sm mx-auto bg-white shadow-[0_10px_30px_rgba(0,0,0,0.15)] rounded-lg p-6 mt-4`}>
      <button
        className={`p-4 ${isOpen ? "mb-4" : "mb-2"} w-full rounded-md text-center shadow-lg bg-blue-500 text-white font-medium hover:bg-blue-600`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? 'Hide' : 'Show'} Synthetic Assets ({syntheticAssets.length})
      </button>
      
      <div
        ref={contentRef}
        style={{
          maxHeight: isOpen ? `${contentRef.current?.scrollHeight}px` : '0px',
        }}
        className={`overflow-hidden transition-max-height duration-500 ease-in-out`}
      >
        <div className="space-y-3">
          {syntheticAssets.map((asset) => (
            <div key={asset.id} className="border border-gray-200 rounded-md p-3 bg-gray-50">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <h4 className="font-semibold text-sm">{asset.symbol}</h4>
                  <div className="text-xs text-gray-600 mt-1">
                    <p>Broker: {asset.broker.toUpperCase()}</p>
                    {asset.account && <p>Account: {asset.account}</p>}
                    <p>Side: {formatSideForDisplay(asset.side)}</p>
                    {asset.broker === 'ib' && (
                      <>
                        <p>Currency: {formatFieldForDisplay(asset.currency)}</p>
                        <p>Exchange: {formatFieldForDisplay(asset.exchange)}</p>
                        <p>Sectype: {formatFieldForDisplay(asset.sectype)}</p>
                      </>
                    )}
                    {asset.broker === 'okx' && (
                      <p>Leverage: {formatFieldForDisplay(asset.leverage)}</p>
                    )}
                  </div>
                </div>
                
                <div className={`px-2 py-1 rounded text-xs ml-2 ${
                  asset.is_ready_for_transfer 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {asset.is_ready_for_transfer ? 'Ready' : 'Incomplete'}
                </div>
              </div>
              
              <div className="flex space-x-1">
                <button
                  onClick={() => handleEdit(asset)}
                  className="flex-1 px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                >
                  Edit
                </button>
                
                <button
                  onClick={() => handleTransfer(asset)}
                  disabled={!asset.is_ready_for_transfer}
                  className={`flex-1 px-2 py-1 rounded text-xs ${
                    asset.is_ready_for_transfer
                      ? 'bg-green-500 text-white hover:bg-green-600'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Transfer
                </button>
                
                <button
                  onClick={() => handleDelete(asset)}
                  className="flex-1 px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {syntheticAssets.some(asset => !asset.is_ready_for_transfer) && (
        <div className={`pt-4 bg-white font-bold text-center text-opacity-65 text-orange-500`}>
          ASSETS NEED CONFIGURATION
        </div>
      )}
      
      {renderEditForm()}
    </div>
  );
};

export default SyntheticAssetsManager;